/**
 * Shared hero banner for Student Loan Contribution pages.
 *
 * Displays a gradient hero section with greeting, benefit progress tracker,
 * and metric cards. Supports three layout variants based on plan limits:
 *
 * - Variant 1: Annual limit — Progress tracker + 2 cards (annual limit on plan)
 * - Variant 2: Lifetime limit — Progress tracker + 3 cards (lifetime limit on plan)
 * - Variant 3: No limit — No tracker, 4 equal cards (no annual/lifetime limit)
 * - Variant 4: Both limits — Progress tracker (annual) + 3 cards including lifetime
 *
 * Used by both the demo loans index page and the production contribution history.
 */
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import style from 'ember-style-modifier/modifiers/style';
import { eq, or } from 'tio-ui/utilities';
import Heading from 'tio-reimagined/components/heading';
import { formatCurrency } from 'tio-reimagined/format';
import {
  PhGraduationCap,
  PhInfo,
  PhHandshake,
  PhCheckCircle,
  PhPaperPlaneTilt,
  PhMoneyWavy,
  PhTrophy,
  PhWarningCircle,
} from 'ember-phosphor-icons';

export interface ContributionHeroSignature {
  Args: {
    /** Time-based greeting, e.g. "Good morning" */
    greeting: string;
    /** User's first name */
    firstName: string;
    /**
     * Layout variant driven by plan limits:
     * 1 = annual limit — tracker + 2 cards
     * 2 = lifetime limit — tracker + 3 cards
     * 3 = no limit — no tracker, 4 equal cards
     * 4 = both limits — tracker (annual) + 3 cards including lifetime
     */
    variant: 1 | 2 | 3 | 4;
    /** Estimated remaining benefit for the year (variants 1 & 2) */
    estimatedRemaining?: number;
    /** Benefit year progress percentage 0–100 (variants 1 & 2) */
    benefitYearProgressPercent?: number;
    /** Benefit year range string, e.g. "10/01/25-10/01/26" */
    benefitYearRange?: string;
    /** The applicable limit amount in dollars — annual or lifetime (variants 1 & 2) */
    limitAmount?: number;
    /** Label for the limit type — "annual limit" or "lifetime limit" (variants 1 & 2) */
    limitLabel?: string;
    /** Employer contribution year-to-date (all variants) */
    employerContribution: number;
    /** Lifetime employer benefit total (variants 2 & 3) */
    lifetimeBenefit?: number;
    /** Next contribution amount */
    nextContribution: number;
    /** Next contribution date string, e.g. "01/2026" */
    nextContributionDate: string;
    /** Last payment amount (variant 3 only) */
    lastPaymentAmount?: number;
    /** Formatted last payment date (variant 3 only) */
    lastPaymentFormatted?: string;
    /** Current year for "{year} Contribution" label (variant 3) */
    currentYear?: number;
    /** Number of payments made this benefit year (variant 3) */
    paymentCount?: number;
    /** Formatted date for "as of" subtext on lifetime card (variant 3) */
    lifetimeAsOfDate?: string;
    /** Whether the next contribution is blocked (validation errors) */
    blocked?: boolean;
    /** Lifetime limit amount in dollars (variant 4 — both limits) */
    lifetimeLimitAmount?: number;
    /** Callback for "view benefit" link */
    onViewBenefit?: () => void;
  };
}

export default class ContributionHero extends Component<ContributionHeroSignature> {
  handleViewBenefit = () => {
    this.args.onViewBenefit?.();
  };

  <template>
    <div class="hero-gradient-bg rounded-3xl p-6 relative overflow-hidden">
      {{! Decorative Circles }}
      <div
        class="absolute left-[64%] top-6.5 w-12 h-12 rounded-full pointer-events-none bg-purple-500/10 hero-float"
      ></div>
      <div
        class="absolute left-[66%] -top-14.5 w-30 h-30 rounded-full pointer-events-none bg-purple-500/5 hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="absolute left-[77%] top-1.75 w-16 h-16 rounded-full pointer-events-none bg-purple-500/10 hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header with Greeting }}
      <div class="flex items-start justify-between mb-6">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 text-gray-600 text-base">
            <PhGraduationCap @size={{20}} />
            <span class="font-medium">{{@greeting}}, {{@firstName}}</span>
          </div>
          <Heading @level={{2}} class="text-2xl font-semibold text-gray-900 leading-tight">
            {{t "slr.reimagined.your_student_loan_contributions"}}
          </Heading>
        </div>
        <button
          type="button"
          class="text-base font-semibold text-gray-900 hover:text-purple-700 underline decoration-solid"
          {{on "click" this.handleViewBenefit}}
        >
          {{t "slr.reimagined.view_benefit"}}
        </button>
      </div>

      {{! Metric Grid — layout determined by @variant }}
      {{#if (eq @variant 3)}}
        {{! Variant 3: No limit — 4 equal cards (matches Figma "SLR Hero/No Limit/Active") }}
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-6">
          {{! Year Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[136px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.year_contribution" year=@currentYear}}
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @employerContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{@paymentCount}}
                {{t "slr.reimagined.contribution_count" count=@paymentCount}}
              </span>
              <PhMoneyWavy @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Lifetime Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[136px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.lifetime_contribution"}}
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @lifetimeBenefit 0)}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{#if @lifetimeAsOfDate}}
                  {{t "slr.reimagined.as_of_date" date=@lifetimeAsOfDate}}
                {{else}}
                  {{t "slr.reimagined.over_lifetime"}}
                {{/if}}
              </span>
              <PhTrophy @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{! Last Contribution Card }}
          {{#if @lastPaymentAmount}}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[136px]">
              <div class="flex flex-col gap-3">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.last_contribution"}}
                </div>
                <div class="text-3xl font-semibold leading-none text-gray-900">
                  {{formatCurrency @lastPaymentAmount}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{t "slr.reimagined.sent_date" date=@lastPaymentFormatted}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col justify-between min-h-[136px]">
            <div class="flex flex-col gap-3">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-3xl font-semibold leading-none
                  {{if @blocked 'text-red-600' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{#if @blocked}}
                  {{t "slr.reimagined.unable_to_process"}}
                {{else}}
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                {{/if}}
              </span>
              {{#if @blocked}}
                <PhWarningCircle @size={{16}} class="text-red-600" />
              {{else}}
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>
      {{else}}
        {{! Variants 1, 2 & 4: Annual/lifetime/both limits — Tracker + metric cards }}
        <div
          class="grid grid-cols-1 md:grid-cols-2
            {{if (or (eq @variant 2) (eq @variant 4)) 'xl:grid-cols-4' 'lg:grid-cols-3'}}
            gap-4 lg:gap-6"
        >
          {{! Tracker Card — Remaining Benefit }}
          <div class="md:col-span-2 lg:col-span-1 bg-white/50 rounded-2xl p-4 min-w-0">
            <div class="flex flex-col gap-2.5 pt-2">
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency (or @estimatedRemaining 0)}}
              </div>
              <div class="flex items-center gap-1 h-4">
                <span class="text-sm font-medium leading-tight text-gray-600">
                  {{t "slr.reimagined.estimated_remaining_benefit"}}
                </span>
                <PhInfo @size={{16}} class="text-gray-600" aria-hidden="true" />
              </div>
            </div>

            {{! Progress Bar }}
            <div class="flex flex-col gap-2 mt-6">
              <div class="h-3 bg-gray-900/15 rounded-2xl overflow-hidden">
                <div
                  class="h-full bg-gray-900 rounded-2xl transition-all"
                  {{style width=(concat (or @benefitYearProgressPercent 0) "%")}}
                ></div>
              </div>
              <div class="flex flex-col gap-1 text-sm px-0.5 leading-none">
                <span class="text-gray-600">
                  <span class="lowercase font-normal">{{t "slr.reimagined.benefit_year"}}</span>
                  <span class="font-semibold"> {{@benefitYearRange}}</span>
                </span>
                <span class="text-gray-700 tracking-wide">
                  <span class="font-medium text-gray-600">{{@limitLabel}}</span>
                  <span class="font-semibold"> {{formatCurrency (or @limitAmount 0)}}</span>
                </span>
              </div>
            </div>
          </div>

          {{! Employer Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.employer_contribution"}}
              </div>
              <div class="text-3xl font-semibold leading-none text-gray-900">
                {{formatCurrency @employerContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{t "slr.reimagined.this_benefit_year"}}
              </span>
              <PhHandshake @size={{16}} class="text-gray-600" />
            </div>
          </div>

          {{#if (or (eq @variant 2) (eq @variant 4))}}
            {{! Total Contribution Card (Lifetime limit or both limits variant) }}
            <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
              <div class="flex flex-col gap-2">
                <div class="text-base font-medium leading-none text-gray-700">
                  {{t "slr.reimagined.total_contribution"}}
                </div>
                <div class="text-3xl font-semibold leading-none text-gray-900">
                  {{formatCurrency (or @lifetimeBenefit 0)}}
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium leading-none text-gray-600">
                  {{#if (eq @variant 4)}}
                    {{t "slr.reimagined.lifetime_limit"}}
                    {{formatCurrency (or @lifetimeLimitAmount 0)}}
                  {{else}}
                    {{t "slr.reimagined.lifetime"}}
                  {{/if}}
                </span>
                <PhCheckCircle @size={{16}} class="text-gray-600" />
              </div>
            </div>
          {{/if}}

          {{! Next Contribution Card }}
          <div class="bg-white/50 rounded-2xl p-4 flex flex-col gap-3">
            <div class="flex flex-col gap-2">
              <div class="text-base font-medium leading-none text-gray-700">
                {{t "slr.reimagined.next_contribution"}}
              </div>
              <div
                class="text-3xl font-semibold leading-none
                  {{if @blocked 'text-red-600' 'text-gray-900'}}"
              >
                {{formatCurrency @nextContribution}}
              </div>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-medium leading-none text-gray-600">
                {{#if @blocked}}
                  {{t "slr.reimagined.unable_to_process"}}
                {{else}}
                  {{t "slr.reimagined.expected_date" date=@nextContributionDate}}
                {{/if}}
              </span>
              {{#if @blocked}}
                <PhWarningCircle @size={{16}} class="text-red-600" />
              {{else}}
                <PhPaperPlaneTilt @size={{16}} class="text-gray-600" />
              {{/if}}
            </div>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
